// 共用攔截器：所有 Request 實例（authRequest / usersRequest / qaRequest）皆套用相同的
// 回應錯誤處理；需要驗證的實例額外套用 request 攔截器，於每次請求時帶入最新的 Bearer Token。
import { getCookie } from '../../utils/cookie';
import { useToast } from '../../composables/useToast';
import { useSession } from '../../composables/useSession';
import { reLogin, refreshToken } from '../../services/auth';

// 需要驗證的實例：每次請求前從 cookie 讀取最新 access token 並帶上 Authorization。
// 以 request 攔截器讀 cookie，token 刷新後即時生效，不依賴 axios.defaults 的全域設定。
export function attachAuthInterceptor(instance) {
  instance.interceptors.request.use((config) => {
    const token = getCookie('rpg_temp_token');
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

// 統一結果處理：
// - 401：access token 過期，用 refresh token 換發新 token 後「重試原請求一次」；refresh 也失敗才 reLogin
// - 403：已登入但無權限（未購買課程回 NO_QA、CSRF 失效等），重新登入無法解決，不走 reLogin，依 errorCode 處理
// - errorCode：NO_QA 隱藏問答區、GO_LOGIN 重新登入
// 其餘錯誤不 re-throw：回傳 undefined 讓 promise resolve，維持既有元件的處理方式。
export function attachResponseInterceptor(instance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { postMessage } = useToast();
      const { handleQANoShow } = useSession();
      if (!error.response) return undefined; // 無回應（網路錯誤等）維持原行為

      const { config } = error;
      const errorData = error.response.data;
      const errorCode = errorData?.code || '';
      const errorStatus = error.response.status;

      // refresh 端點自身的錯誤不再進入刷新流程（避免遞迴），交由呼叫端 refreshToken 判斷
      if (config?._isRefreshCall) return undefined;

      // 401：先用 refresh token 換發新 token，再重試原請求（每個請求只重試一次）
      // 並發的 401 由 refreshToken() 的共用鎖合併成一次換新（含同頁其他 bundle，見 services/auth.js）
      if (errorStatus === 401 && !config._retry) {
        config._retry = true;
        const newToken = await refreshToken();
        if (newToken) {
          return instance(config); // request 攔截器會自 cookie 帶上新 token
        }
        reLogin(); // refresh token 也過期／被撤銷 → 倒回登入
        return undefined;
      }

      // 已重試過仍 401：refresh 也救不回來，直接倒回登入頁
      if (errorStatus === 401) {
        reLogin();
        return undefined;
      }

      // 403 不可 reLogin：使用者系統會以「已登入」身分把人導回原章節，再次 403 便形成無限導頁；
      // 交由下方 errorCode 處理（未購買課程時後端回 403 + NO_QA，隱藏問答區即可）
      if (errorCode !== '') {
        if (errorCode === 'NO_QA') {
          handleQANoShow();
        }
        if (errorCode === 'GO_LOGIN') {
          reLogin();
        }
      } else {
        postMessage('danger', '出現錯誤', `${error?.response?.status} - ${error?.response?.data.message}`);
      }
      return undefined;
    },
  );
}
